import React, { useCallback, useMemo, useState } from "react";
import { View } from "react-native";
import { StackScreenProps } from "@react-navigation/stack";
import { useNavigation } from "@react-navigation/native";
import { useAtomValue } from "jotai";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { editVacationCallback } from "../../atoms/core";
import ScreenCard from "../ScreenCard";
import { useI18n } from "../../hooks/i18n-hooks";
import { useTheme } from "../../hooks/use-theme";
import { useIsScreenSizeMin } from "../../hooks/useIsScreenSizeMin";
import ModalDialogFrame from "../../components/ModalDialogFrame";
import { EarningType } from "../../__generated__/graphql";
import { toFixedIfNecessary } from "../../utils";
import SelectField from "../../components/SelectField";
import ButtonField from "../../components/ButtonField";
import Switch from "../../ui_atoms/Switch";
import Gap from "../../ui_atoms/Gap";
import { BodySmallText, TitleMediumText } from "../../components/FormattedText";
import { BubbleTalk } from "../../components/BubbleTalk";
import DocuIllSvg from "../../assets/images/docu_ill.svg";
import VStack from "../../ui_atoms/VStack";

type VacationEditorNavScreenProps = StackScreenProps<RootStackParamList, "VacationEditorNavScreen">;

const VacationEditorNavScreen: React.FC<VacationEditorNavScreenProps> = ({ route }) => {
    const {
        vacationDays: initialVacationDays,
        payVacationIndemnityWithSalary: initialPayWithSalary,
        isCagnotteMandatory,
        isVacationTracked,
        minVacationDays,
        contractPercentage,
        earningType,
    } = route.params;
    const i18n = useI18n();
    const { colors, gridSize } = useTheme();
    const isLarge = useIsScreenSizeMin("large");
    const navigation = useNavigation();
    const insets = useSafeAreaInsets();
    const callback = useAtomValue(editVacationCallback);

    const [vacationDays, setVacationDays] = useState<number | undefined>(initialVacationDays);
    // When vacations are not tracked the indemnity is always paid with salary; when cagnotting is
    // mandatory it is always kept in the cagnotte (not paid directly).
    const [payVacationIndemnityWithSalary, setPayVacationIndemnityWithSalary] = useState<boolean>(
        !isVacationTracked ? true : isCagnotteMandatory ? false : initialPayWithSalary,
    );

    const vacationDaysItems = useMemo(() => {
        const min = minVacationDays || 20;
        return new Array(41 - min).fill(0).map((_, index) => {
            const days = index + min;
            const fullWeek = days % 5 === 0;
            const nbWeeks = Math.floor(days / 5);
            return {
                label: i18n.t(
                    `screens.user.create_mission_salary.vacation_days${
                        fullWeek ? "_full_week" : ""
                    }_format`,
                    {
                        days,
                        nbWeeks,
                    },
                ),
                value: days,
            };
        });
    }, [i18n, minVacationDays]);

    const displayVacationPercentage =
        contractPercentage !== 100 && earningType !== EarningType.Package;

    // The cagnotte switch is shown for every non-monthly mission. It is read-only when cagnotting
    // is mandatory (forced ON) or when vacations are not tracked for this mission type — in the
    // latter case the indemnity is always paid with the salary, so the switch is forced OFF.
    const showCagnotte = earningType !== EarningType.Monthly;
    const cagnotteDisabled = isCagnotteMandatory || !isVacationTracked;
    const cagnotte = isVacationTracked && (isCagnotteMandatory || !payVacationIndemnityWithSalary);
    const cagnotteLegendKey = !isVacationTracked
        ? "screens.user.create_mission_salary.cagnotteVacationsNonTrackedLegend"
        : isCagnotteMandatory
          ? "screens.user.create_mission_salary.cagnotteVacationsObligatoireLegend"
          : "screens.user.create_mission_salary.cagnotteVacationsLegend";

    const onClose = useCallback(() => {
        if (callback.func) {
            void callback.func({ vacationDays, payVacationIndemnityWithSalary });
        } else {
            navigation.goBack();
        }
    }, [callback, vacationDays, payVacationIndemnityWithSalary, navigation]);

    return (
        <ModalDialogFrame>
            <ScreenCard
                style={[{ paddingTop: insets.top }]}
                relativeActions={isLarge}
                buttons={[
                    {
                        title: i18n.t("close"),
                        onPress: onClose,
                        mode: "outlined",
                    },
                ]}
            >
                <VStack h={1}>
                    <Gap gap={0.5} />
                    <BubbleTalk
                        personImageComponent={<DocuIllSvg width="100%" height="100%" />}
                        personSize={44}
                        position="top-center"
                    >
                        <TitleMediumText>
                            {i18n.t("screens.user.create_mission_salary.vacationEditorTitle")}
                        </TitleMediumText>
                    </BubbleTalk>
                    <Gap gap={1.5} />
                    <SelectField
                        style={{
                            marginHorizontal: -gridSize,
                            paddingHorizontal: gridSize,
                        }}
                        asListAction={vacationDays ? "primary" : "secondary"}
                        onSelect={({ value }) => {
                            if (value) {
                                setVacationDays(value);
                            }
                        }}
                        data={vacationDaysItems}
                        placeholder={i18n.t("screens.user.create_mission_salary.setVacationDays")}
                        value={vacationDays}
                        right="gear"
                        info={
                            displayVacationPercentage && (
                                <BodySmallText color={colors.onSurfaceDisabled}>
                                    {i18n.t(
                                        "screens.user.create_mission_salary.vacation_percentage",
                                        {
                                            percent: contractPercentage,
                                            count: toFixedIfNecessary(
                                                ((vacationDays || 0) * contractPercentage) / 100,
                                                2,
                                            ),
                                        },
                                    )}
                                </BodySmallText>
                            )
                        }
                    />
                    {showCagnotte && (
                        <>
                            <Gap gap={1} />
                            <TitleMediumText>
                                {i18n.t(
                                    "screens.user.create_mission_salary.cagnotteVacationsTitle",
                                )}
                            </TitleMediumText>
                            <Gap gap={0.5} />
                            <ButtonField
                                style={{
                                    marginHorizontal: -gridSize,
                                    paddingHorizontal: gridSize,
                                }}
                                asListAction="primary"
                                valueColor={cagnotteDisabled ? colors.onSurfaceDisabled : undefined}
                                disabled={cagnotteDisabled}
                                text={i18n.t(
                                    "screens.user.create_mission_salary.cagnotteVacationsAction",
                                )}
                                right={
                                    <View pointerEvents="none">
                                        <Switch
                                            value={cagnotte}
                                            size={44}
                                            disabled={cagnotteDisabled}
                                        />
                                    </View>
                                }
                                onPress={
                                    cagnotteDisabled
                                        ? undefined
                                        : () => setPayVacationIndemnityWithSalary((v) => !v)
                                }
                                info={
                                    <BodySmallText color={colors.onSurfaceDisabled}>
                                        {i18n.t(cagnotteLegendKey)}
                                    </BodySmallText>
                                }
                            />
                        </>
                    )}
                </VStack>
            </ScreenCard>
        </ModalDialogFrame>
    );
};

export default VacationEditorNavScreen;
